import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { GetClientDraftsResponse } from '@/types/clientDraftsApiTypes';
import { useDebouncedValue } from '@/hooks/useDebounceValue';

export const CLIENTS_DRAFTS_CACHE_KEY = 'clients-drafts';

interface UseGetClientDraftsProps {
  perPage?: number;
  enabled?: boolean;
}

export const useGetClientDrafts = ({
  perPage = 24,
  enabled = true
}: UseGetClientDraftsProps = {}) => {
  const [searchQuery] = useSearchQuery();
  const { doFetch } = useFetch<void, GetClientDraftsResponse>();
  const search = searchQuery.get('search') || '';

  const debouncedSearch = useDebouncedValue(search, 500);

  const buildUrl = useCallback(
    (pageParam: number) => {
      const params = new URLSearchParams();

      if (debouncedSearch) params.append('search', debouncedSearch);
      if (perPage) params.append('per_page', perPage.toString());
      if (pageParam) params.append('page', pageParam.toString());

      return `/api/clients/drafts?${params.toString()}`;
    },
    [debouncedSearch, perPage]
  );

  const queryKey = [CLIENTS_DRAFTS_CACHE_KEY, debouncedSearch, perPage];

  const fetchDrafts = async ({ pageParam = 1 }) => {
    const url = buildUrl(pageParam);
    const results = await doFetch(url);

    return {
      ...results,
      nextCursor:
        results.pagination?.last_page !== results.pagination?.current_page
          ? pageParam + 1
          : undefined,
    };
  };

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn: fetchDrafts,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 1,
    enabled: enabled,
  });

  return {
    ...queryResult,
    data: queryResult.data?.pages.flatMap((page) => page.data) ?? [],
    pagination: queryResult.data?.pages.at(-1)?.pagination,
  };
};
